import React from 'react';
import { MapPin, Tag, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { cleanTitle, keyDataSummary, stripHtml, formatDate, CATEGORY_MAP, DOC_TYPE_BADGE, STATUS_CLASSES, STATUS_LABELS } from './constants';

export function GroupedView({ entries, groupBy, setGroupBy, collapsedGroups, setCollapsedGroups, navigate, locations, scoresMap, duplicates }) {
  const locationMap = {};
  (locations || []).forEach(l => { locationMap[l.location_id] = l.name; });

  const groups = {};
  for (const entry of entries) {
    let key;
    if (groupBy === 'category') {
      key = entry.category || '_geen';
    } else {
      key = entry.location_id || '_onbekend';
      if (key === 'all' || key === 'holding') key = 'all_holding';
    }
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
  }

  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (groupBy === 'location') {
      if (a === 'all_holding') return 1;
      if (b === 'all_holding') return -1;
    }
    return groups[b].length - groups[a].length;
  });

  const getScoreColor = (score) => {
    if (score == null) return 'text-slate-400';
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-[#2E75B6]';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  for (const key of sortedKeys) {
    groups[key].sort((a, b) => {
      const aPending = a.entry_status === 'pending_review' || a.entry_status === 'pending' ? 0 : 1;
      const bPending = b.entry_status === 'pending_review' || b.entry_status === 'pending' ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
  }

  const getGroupLabel = (key) => {
    if (key === '_geen') return 'Geen categorie';
    if (key === '_onbekend') return 'Onbekend';
    if (groupBy === 'category') return CATEGORY_MAP[key] || key;
    if (key === 'all_holding') return 'Alle locaties / Holding';
    return locationMap[key] || key;
  };

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-3" data-testid="kennisbank-grouped">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Groeperen op</span>
        <button data-testid="group-by-location" onClick={() => setGroupBy('location')}
          className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${groupBy === 'location' ? 'bg-[#2E75B6] text-white border-[#2E75B6]' : 'border-slate-200 text-slate-500 hover:text-slate-700'}`}>
          Locatie
        </button>
        <button data-testid="group-by-category" onClick={() => setGroupBy('category')}
          className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${groupBy === 'category' ? 'bg-[#2E75B6] text-white border-[#2E75B6]' : 'border-slate-200 text-slate-500 hover:text-slate-700'}`}>
          Categorie
        </button>
      </div>

      {sortedKeys.map(key => {
        const items = groups[key];
        const collapsed = collapsedGroups[key];
        const hasPending = items.some(e => e.entry_status === 'pending_review' || e.entry_status === 'pending');

        return (
          <div key={key} className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid={`group-${key}`}>
            <button onClick={() => toggleGroup(key)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-2">
                {groupBy === 'location' ? <MapPin className="w-3.5 h-3.5 text-slate-400" /> : <Tag className="w-3.5 h-3.5 text-slate-400" />}
                <span className="text-sm font-semibold text-slate-800">{getGroupLabel(key)}</span>
                <span className="text-[11px] text-slate-500 font-medium ml-1">
                  ({items.length} {items.length === 1 ? 'entry' : 'entries'})
                </span>
                {groupBy === 'location' && key !== 'all_holding' && key !== '_onbekend' && scoresMap && scoresMap[key] && (
                  <span className="text-[11px] font-medium text-slate-400 ml-2 flex items-center">
                    &middot; Compliance: <span className={`ml-1 ${getScoreColor(scoresMap[key].score)}`}>{Math.round(scoresMap[key].score)}%</span>
                  </span>
                )}
                {hasPending && <span className="w-2 h-2 rounded-full bg-amber-400 ml-1" />}
              </div>
              {collapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
            </button>
            {!collapsed && (
              <div className="border-t border-slate-100 divide-y divide-slate-50">
                {items.map(entry => {
                  const isPending = entry.entry_status === 'pending_review' || entry.entry_status === 'pending';
                  return (
                    <button key={entry.entry_id} data-testid={`grouped-entry-${entry.entry_id}`}
                      onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors group flex items-center gap-3 ${isPending ? 'bg-amber-50/50' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-slate-800 truncate group-hover:text-[#2E75B6] transition-colors">
                            {cleanTitle(entry.title)}
                          </h4>
                          {duplicates.has(entry.entry_id) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium">Mogelijk duplicaat</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {entry.key_data?.document_type ? (keyDataSummary(entry.key_data) || stripHtml(entry.content)) : stripHtml(entry.content)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {entry.key_data?.document_type && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600">{DOC_TYPE_BADGE[entry.key_data.document_type] || 'Doc'}</span>
                        )}
                        {entry.ai_normalize_suggestions?.length > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600"><Lightbulb className="w-2.5 h-2.5 inline" /> {entry.ai_normalize_suggestions.length}</span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLASSES[entry.entry_status] || 'badge-active'}`}>
                          {STATUS_LABELS[entry.entry_status] || entry.entry_status}
                        </span>
                        <span className="text-[10px] text-slate-400">{formatDate(entry.updated_at)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
